import { useState, useEffect } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { useDomain } from '../lib/domain';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Loader2, Image, Home, LogIn, UserPlus, Save, AlertCircle, Type, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const FrontendSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const domainConfig = useDomain();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    landing_page_enabled: true,
    landing_image: '',
    login_image: '',
    register_image: '',
    site_name: 'OMS Portal'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/frontend');
      if (response.data.success) {
        setSettings({
          landing_page_enabled: response.data.data.landing_page_enabled ?? true,
          landing_image: response.data.data.landing_image || '',
          login_image: response.data.data.login_image || '',
          register_image: response.data.data.register_image || '',
          site_name: response.data.data.site_name || 'OMS Portal'
        });
      }
    } catch (error) {
      console.error('Error fetching frontend settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/settings/frontend', settings);
      if (response.data.success) {
        toast.success(t('admin.settingsSaved'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('common.accessDenied')}</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">{t('admin.superAdminRequired')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('admin.frontendSettings')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('admin.frontendSettingsDescription')}
        </p>
      </div>

      <div className="grid gap-6">
        {/* Site Name */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Type className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">{t('admin.siteName')}</CardTitle>
                <CardDescription>{t('admin.siteNameDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>{t('admin.siteNameLabel')}</Label>
              <Input
                value={settings.site_name}
                onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                placeholder="OMS Portal"
                maxLength={50}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('admin.siteNameHelp')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Landing Page Toggle - Sadece ana domain için geçerli */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <Home className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </div>
              <div>
                <CardTitle className="text-lg">{t('admin.landingPageToggle')}</CardTitle>
                <CardDescription>{t('admin.landingPageToggleDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bilgi kutusu - firma domain'lerinde landing page olmadığını belirt */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg mb-4 border border-blue-100">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Not: Bu ayar sadece ana domain (hrportal.be) için geçerlidir.</p>
                <p className="mt-1 text-blue-600">Firma domain'lerinde (hr.firma.com gibi) ana sayfa gösterilmez, kullanıcılar doğrudan giriş sayfasına yönlendirilir.</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-200">{t('admin.enableLandingPage')}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin.landingPageNote')}</p>
              </div>
              <Switch
                checked={settings.landing_page_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, landing_page_enabled: checked })}
              />
            </div>
            {!settings.landing_page_enabled && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700">
                <AlertCircle className="h-4 w-4 inline-block mr-2" />
                {t('admin.landingDisabledWarning')}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Images */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Image className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">{t('admin.pageImages')}</CardTitle>
                <CardDescription>{t('admin.pageImagesDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Landing Page Image */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Home className="h-4 w-4 text-slate-400" />
                {t('admin.landingPageImage')}
              </Label>
              <Input
                value={settings.landing_image}
                onChange={(e) => setSettings({ ...settings, landing_image: e.target.value })}
                placeholder="https://example.com/landing-hero.jpg"
              />
              {settings.landing_image && (
                <div className="mt-2 p-2 border rounded-lg">
                  <img 
                    src={settings.landing_image} 
                    alt="Landing preview" 
                    className="h-24 w-full object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Login Page Image */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <LogIn className="h-4 w-4 text-slate-400" />
                {t('admin.loginPageImage')}
              </Label>
              <Input
                value={settings.login_image}
                onChange={(e) => setSettings({ ...settings, login_image: e.target.value })}
                placeholder="https://example.com/login-bg.jpg"
              />
              {settings.login_image && (
                <div className="mt-2 p-2 border rounded-lg">
                  <img 
                    src={settings.login_image} 
                    alt="Login preview" 
                    className="h-24 w-full object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Register Page Image */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-slate-400" />
                {t('admin.registerPageImage')}
              </Label>
              <Input
                value={settings.register_image}
                onChange={(e) => setSettings({ ...settings, register_image: e.target.value })}
                placeholder="https://example.com/register-bg.jpg"
              />
              {settings.register_image && (
                <div className="mt-2 p-2 border rounded-lg">
                  <img 
                    src={settings.register_image} 
                    alt="Register preview" 
                    className="h-24 w-full object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FrontendSettings;
